import { ref, computed, watch, type MaybeRefOrGetter, toValue } from 'vue';
import type { ProofOfPlayByPlayer } from '@/helpers/api/metrics';
import { getPlayersByDeviceUuids } from '@/helpers/api/player/player';
import { normalizeUuid } from '@/helpers/utils';

export interface PlayerInfo {
  name: string;
  id: number;
}

/**
 * Resolves player_uuid -> { name, id } for the rows currently in byPlayer.
 * Looks up only the UUIDs we haven't seen before; caches forever (with
 * `undefined` for UUIDs the API didn't recognise so we don't re-query them
 * on every poll).
 *
 * Includes a request-id guard so a slow lookup that resolves after a faster
 * subsequent lookup can't overwrite newer data.
 */
export function usePlayerInfoLookup(
  byPlayerSource: MaybeRefOrGetter<ProofOfPlayByPlayer[] | undefined>,
) {
  const playerInfoByUuid = ref<Record<string, PlayerInfo | undefined>>({});

  const byPlayerUuidsSignature = computed(() =>
    (toValue(byPlayerSource) ?? []).map((p) => p.player_uuid).join('|'),
  );

  let latestRequestId = 0;

  watch(
    byPlayerUuidsSignature,
    async () => {
      const myId = ++latestRequestId;
      const rows = toValue(byPlayerSource) ?? [];
      const missing = rows
        .map((p) => p.player_uuid)
        .filter((uuid) => uuid && !(uuid in playerInfoByUuid.value));
      if (!missing.length) return;
      try {
        const players = await getPlayersByDeviceUuids(missing);
        if (myId !== latestRequestId) return; // stale, a newer lookup superseded us

        const byNormalized = new Map<string, PlayerInfo>();
        for (const player of players) {
          if (player.deviceUuid && player.id != null) {
            byNormalized.set(normalizeUuid(player.deviceUuid), {
              name: player.name,
              id: player.id,
            });
          }
        }
        const next = { ...playerInfoByUuid.value };
        for (const uuid of missing) {
          // Store undefined for unmatched UUIDs so we don't retry every poll.
          next[uuid] = byNormalized.get(normalizeUuid(uuid));
        }
        playerInfoByUuid.value = next;
      } catch {
        // Silent: the row falls back to showing the raw UUID.
      }
    },
    { immediate: true },
  );

  return { playerInfoByUuid };
}
